import z from "zod";
import { Network, NetworkId } from "./network";

export enum Chain {
  ETH = "eth",
  ARB = "arb",
  FLOW = "flow",
  MATIC = "matic",
  OPT = "opt",
  SOLANA = "solana",
  ASTAR = "astar",
  POLYGONZKEVM = "polygonzkevm",
  STARKNET = "starknet",
  BASE = "base",
  ZKSYNC = "zksync",
  ZORA = "zora",
  FRAX = "frax",
  BNB = "bnb",
  AVAX = "avax",
  ARBNOVA = "arbnova",
  ALCHEMY = "alchemy",
  WORLDCHAIN = "worldchain",
  ZETACHAIN = "zetachain",
  BLAST = "blast",
  SCROLL = "scroll",
  LINEA = "linea",
  GNOSIS = "gnosis",
  FANTOM = "fantom",
  MANTLE = "mantle",
  CELO = "celo",
  BERACHAIN = "berachain",
  METIS = "metis",
  OPBNB = "opbnb",
  POLYNOMIAL = "polynomial",
  SHAPE = "shape",
  BOB = "bob",
  CROSSFI = "crossfi",
  IOTA = "iota",
  MODE = "mode",
  MOONBASE = "moonbase",
  MOONBEAM = "moonbeam",
  MOONRIVER = "moonriver",
  PALM = "palm",
  SHIDEN = "shiden",
  SEI = "sei",
  KINTO = "kinto",
  PLUME = "plume",
  ANIME = "anime",
  RACE = "race",
  APECHAIN = "apechain",
  APEX = "apex",
  BOSS = "boss",
  LENS = "lens",
  SONEIUM = "soneium",
}

/**
 * Proto enum value (https://github.com/OMGWINNING/protobufs/blob/master/src/main/proto/common.proto#L9)
 * We should prioritize removing this and migrating to dynamic ChainId.
 */
export enum ProtoChain {
  ETH = 1,
  ARB = 3,
  FLOW = 4,
  MATIC = 5,
  OPT = 6,
  SOLANA = 8,
  ASTAR = 9,
  POLYGONZKEVM = 10,
  STARKNET = 11,
  BASE = 12,
  ZKSYNC = 13,
  ZORA = 14,
  FRAX = 15,
  BNB = 16,
  AVAX = 17,
  ARBNOVA = 18,
  ALCHEMY = 19,
  WORLDCHAIN = 20,
  ZETACHAIN = 21,
  BLAST = 22,
  SCROLL = 23,
  LINEA = 24,
  GNOSIS = 25,
  FANTOM = 26,
  MANTLE = 27,
  CELO = 28,
  BERACHAIN = 29,
  METIS = 30,
  OPBNB = 31,
  POLYNOMIAL = 32,
  SHAPE = 33,
  BOB = 34,
  CROSSFI = 35,
  IOTA = 36,
  MODE = 37,
  MOONBASE = 38,
  MOONBEAM = 39,
  MOONRIVER = 40,
  PALM = 41,
  SHIDEN = 42,
  SEI = 43,
  KINTO = 44,
  PLUME = 45,
  ANIME = 46,
  RACE = 47,
  APECHAIN = 48,
  APEX = 49,
  BOSS = 50,
  LENS = 51,
  SONEIUM = 52,
}

/**
 * Type alias for valid values of the {@link https://github.com/OMGWINNING/protobufs/blob/master/src/main/proto/common.proto#264 proto ChainIdentifier}
 * (Alchemy-wide chain id).
 *
 * For now, we're syncing this to the naas `Chain` enum keys, but once we're depending on the
 * Alchemy `DynamicConfigService` for Chain config, static typing won't be possible. So this will
 * likely just become a type alias for `string` (`type ChainId = string`), and we'll have to validate
 * values against a set of valid `ChainIds` fetched from the `DynamicConfigService` before casting
 * to `ChainId`.
 */
export type ChainId = keyof typeof Chain;

/**
 * Static set of all valid `ChainIds`. Once we're depending on the Alchemy `DynamicConfigService`,
 * we won't be able to maintain a static set of `ChainIds`, so this object should be used sparingly
 * in the meantime.
 *
 * For now, it's mostly used as a placeholder for `ChainId` validation and in tests.
 */
export const CHAIN_IDS: Set<ChainId> = new Set(Object.keys(Chain) as ChainId[]);

/**
 * Once we're relying on a `DynamicConfigService`, we won't be able to statically validate `ChainIds`, so we'll
 * have to validate incoming string values at runtime against a set of valid `ChainIds` fetched from the `DynamicConfigService`.
 *
 * This factory fn returns a Zod schema that validates a string as a `ChainId`, given a `validator` (most likely a method on
 * a future `DynamicConfigService` that checks whether the value exists in a cache of valid `ChainIds`).
 * @param validator Fn that returns `true` if the `ChainId` is a `ChainId`, `false` otherwise.
 */
export function chainIdSchemaFactory(
  validator: (value: string) => Promise<boolean>,
) {
  return z.string().transform(async (value, ctx) => {
    if (!(await validator(value))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `${value} is not a ChainId`,
      });

      return z.NEVER;
    }
    return value;
  });
}

export interface ChainInfo {
  // Chain enum value used by frontend
  chain: Chain;
  // Alchemy-wide ID of the chain
  id: ChainId;
  // Blockchain enum value used in protobufs
  value: ProtoChain;
  // Display name
  name: string;
  // Description used for marketing purposes
  description: string;
  // All networks that make up this chain, even if not public or deprecated
  networks: Network[];
  networkIds: NetworkId[];
  // Network that is chosen for this chain by default
  defaultNetwork: Network;
  // Order to display the chain in selectors
  order: number;
}
